/**
 * ActivityFeed — shared "Recent Activity" card used on app dashboards.
 *
 * Mode contract (enforced by `frontend/tests/activity_feeds_real_mode.test.js`):
 *   - Demo mode: renders curated synthetic events from DEMO_ACTIVITY_TEMPLATES
 *     so the showcase dashboards look alive.
 *   - Real mode: renders an empty-state ("No recent activity yet"). No synthetic
 *     events leak into an authenticated tenant. When a real backend activity
 *     feed endpoint exists this component can take a `items` prop and skip
 *     the empty state; for now the contract is "no mocks in real mode".
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';
import {
  Activity, FileText, Users, CheckCircle2, Send, XCircle, DollarSign,
  Calendar, Stethoscope, Clock,
} from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_ACTIVITY_TEMPLATES = {
  revq: [
    { icon: Send, color: 'text-blue-600 bg-blue-50', action: 'Claim CLM-2026-048 submitted to UnitedHealth', time: '2 min ago' },
    { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50', action: 'Payment $340.00 posted for CLM-2026-043', time: '8 min ago' },
    { icon: XCircle, color: 'text-red-600 bg-red-50', action: 'Denial received — CLM-2026-044 (CO-4)', time: '15 min ago' },
    { icon: FileText, color: 'text-violet-600 bg-violet-50', action: 'AI scrubbed 3 claims — 1 issue found', time: '22 min ago' },
    { icon: DollarSign, color: 'text-emerald-600 bg-emerald-50', action: 'ERA processed: $1,240.00 from Blue Cross', time: '35 min ago' },
    { icon: Users, color: 'text-slate-600 bg-slate-100', action: 'Patient statement sent to Emily Davis', time: '45 min ago' },
  ],
  lumina: [
    { icon: Stethoscope, color: 'text-violet-600 bg-violet-50', action: 'SOAP note completed for John Smith', time: '3 min ago' },
    { icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50', action: 'Note signed by Dr. Chen — E11.9, I10', time: '10 min ago' },
    { icon: FileText, color: 'text-blue-600 bg-blue-50', action: 'AI coding review: 99214 confirmed', time: '18 min ago' },
    { icon: Calendar, color: 'text-amber-600 bg-amber-50', action: 'Maria Garcia checked in for 9:30 AM', time: '25 min ago' },
    { icon: Activity, color: 'text-red-600 bg-red-50', action: 'Lab alert: HbA1c 9.2% for John Smith', time: '32 min ago' },
    { icon: Users, color: 'text-slate-600 bg-slate-100', action: 'Patient Robert Johnson added to queue', time: '40 min ago' },
  ],
  echo: [
    { icon: Users, color: 'text-blue-600 bg-blue-50', action: 'John Smith checked in — Room 1 assigned', time: '1 min ago' },
    { icon: Calendar, color: 'text-emerald-600 bg-emerald-50', action: 'Appointment confirmed: Emily Davis 10:30 AM', time: '5 min ago' },
    { icon: Clock, color: 'text-amber-600 bg-amber-50', action: 'Wait time alert: Robert Johnson > 20 min', time: '12 min ago' },
    { icon: Activity, color: 'text-violet-600 bg-violet-50', action: 'AI Agent answered call from Amanda Rodriguez', time: '18 min ago' },
    { icon: DollarSign, color: 'text-emerald-600 bg-emerald-50', action: 'Copay $30 collected from John Smith', time: '25 min ago' },
    { icon: Send, color: 'text-blue-600 bg-blue-50', action: 'Reminder sent to 5 upcoming patients', time: '30 min ago' },
  ],
};

const ActivityFeed = ({ app = 'revq', maxItems = 5 }) => {
  const { isDemo } = useAppMode();
  const activities = isDemo ? (DEMO_ACTIVITY_TEMPLATES[app] || []) : [];

  return (
    <Card className="border-slate-200" data-testid="activity-feed">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-slate-500" />
          <CardTitle className="text-base font-semibold">Recent Activity</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {activities.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-6 text-center"
            data-testid="activity-feed-empty"
          >
            <div className="h-9 w-9 rounded-full bg-slate-50 flex items-center justify-center mb-2">
              <Activity className="h-4 w-4 text-slate-300" />
            </div>
            <p className="text-xs text-slate-500">No recent activity yet</p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              Activity will appear here as your team uses the app.
            </p>
          </div>
        ) : (
          activities.slice(0, maxItems).map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={i} className="flex items-start gap-2.5" data-testid={`activity-item-${i}`}>
                <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5", a.color.split(' ')[1])}>
                  <Icon className={cn("h-3.5 w-3.5", a.color.split(' ')[0])} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-700 leading-relaxed">{a.action}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{a.time}</p>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
